import React, { useState, useEffect, useCallback } from 'react';
import { Accordion, Button } from 'react-bootstrap';
import styled from 'styled-components';
import { IoVolumeHigh, IoPause } from 'react-icons/io5';
import audioManager from '../../../utils/audioUtils';

const Element = styled.div`
  line-height: 18px;
  font-size: 13px;
  color: #f4f1de !important;
  white-space: pre-line;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const CustomAccordionTitle = styled.h2`
  color: #f4f1de !important;
  font-weight: 600 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  margin: 0 !important;
  font-size: 16px !important;
`;

const StyledAccordion = styled(Accordion)`
  && {
    .accordion-item {
      background: transparent !important;
      border: none !important;
      margin-bottom: 10px;
    }
    
    .accordion-button {
      background: rgba(244, 241, 222, 0.1) !important;
      border: 1px solid rgba(244, 241, 222, 0.2) !important;
      color: #f4f1de !important;
      border-radius: 8px !important;
      transition: all 0.3s ease !important;
      backdrop-filter: blur(10px) !important;
      padding: 5px 10px !important;
      font-size: 14px !important;
      box-shadow: none !important;
      
      &:hover {
        background: rgba(244, 241, 222, 0.15) !important;
        border-color: rgba(244, 241, 222, 0.3) !important;
        transform: translateY(-1px) !important;
      }
      
      &:focus {
        background: rgba(244, 241, 222, 0.15) !important;
        border-color: rgba(244, 241, 222, 0.4) !important;
        box-shadow: 0 0 0 0.2rem rgba(244, 241, 222, 0.25) !important;
        color: #f4f1de !important;
      }
      
      &:not(.collapsed) {
        background: rgba(244, 241, 222, 0.15) !important;
        border-color: rgba(244, 241, 222, 0.4) !important;
        color: #f4f1de !important;
      }
      
      &::after {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23f4f1de' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e") !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 16px 12px !important;
        width: 16px !important;
        height: 12px !important;
        transition: all 0.3s ease !important;
        filter: none !important;
      }
      
      &:hover::after {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23f4f1de' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e") !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 16px 12px !important;
      }
      
      &.collapsed::after {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23f4f1de' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e") !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 16px 12px !important;
        transform: rotate(0deg) !important;
      }
      
      &:not(.collapsed)::after {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23f4f1de' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 6 7 7 7-7'/%3e%3c/svg%3e") !important;
        background-repeat: no-repeat !important;
        background-position: center !important;
        background-size: 16px 12px !important;
        transform: rotate(180deg) !important;
      }
    }
    
    .accordion-collapse {
      border: none !important;
    }
    
    .accordion-body {
      background: rgba(244, 241, 222, 0.05) !important;
      border: 1px solid rgba(244, 241, 222, 0.1) !important;
      border-top: none !important;
      border-radius: 0 0 8px 8px !important;
      padding: 16px !important;
      color: #f4f1de !important;
      position: relative;
      max-height: 72px !important;
      overflow-y: auto !important;
      height: 72px !important;
      
      /* Custom scrollbar para o accordion */
      &::-webkit-scrollbar {
        width: 6px !important;
      }
      
      &::-webkit-scrollbar-track {
        background: rgba(244, 241, 222, 0.1) !important;
        border-radius: 3px !important;
      }
      
      &::-webkit-scrollbar-thumb {
        background: rgba(244, 241, 222, 0.3) !important;
        border-radius: 3px !important;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: rgba(244, 241, 222, 0.5) !important;
      }
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: 
          radial-gradient(circle at 20% 50%, rgba(244, 241, 222, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 50%, rgba(244, 241, 222, 0.02) 0%, transparent 50%);
        pointer-events: none;
        z-index: 0;
        border-radius: 0 0 8px 8px;
      }
    }
  }
`;

const InstructionsContainer = styled.div`
  margin: 15px 0;
  position: relative;
  z-index: 1;
`;

const AudioButton = styled(Button)`
  background: rgba(244, 241, 222, 0.1) !important;
  border: 1px solid rgba(244, 241, 222, 0.2) !important;
  color: #f4f1de !important;
  border-radius: 50% !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-left: 10px !important;
  transition: all 0.3s ease !important;
  backdrop-filter: blur(10px) !important;
  position: relative !important;
  
  &:hover {
    background: rgba(244, 241, 222, 0.2) !important;
    border-color: rgba(244, 241, 222, 0.4) !important;
    transform: scale(1.05) !important;
  }
  
  &:focus {
    background: rgba(244, 241, 222, 0.2) !important;
    border-color: rgba(244, 241, 222, 0.4) !important;
    box-shadow: 0 0 0 0.2rem rgba(244, 241, 222, 0.25) !important;
    color: #f4f1de !important;
  }
  
  &:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
  
  &.playing {
    background: rgba(76, 175, 80, 0.2) !important;
    border-color: rgba(76, 175, 80, 0.4) !important;
    animation: pulse 2s infinite !important;
  }
  
  &.paused {
    background: rgba(255, 152, 0, 0.2) !important;
    border-color: rgba(255, 152, 0, 0.4) !important;
  }
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4) !important;
    }
    70% {
      box-shadow: 0 0 0 10px rgba(76, 175, 80, 0) !important;
    }
    100% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0) !important;
    }
  }
`;

const AudioControlsContainer = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 5px !important;
`;

const SpeedControl = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 3px !important;
  margin-left: 5px !important;
`;

const VolumeControl = styled.div`
  display: flex !important;
  align-items: center !important;
  gap: 3px !important;
  margin-left: 5px !important;
`;

const VolumeTestButton = styled(Button)`
  background: rgba(244, 241, 222, 0.1) !important;
  border: 1px solid rgba(244, 241, 222, 0.2) !important;
  color: #f4f1de !important;
  border-radius: 4px !important;
  padding: 2px 4px !important;
  font-size: 9px !important;
  min-width: 20px !important;
  height: 20px !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(244, 241, 222, 0.2) !important;
    border-color: rgba(244, 241, 222, 0.4) !important;
  }
`;

const VolumeSlider = styled.input`
  width: 60px !important;
  height: 4px !important;
  background: rgba(244, 241, 222, 0.2) !important;
  border-radius: 2px !important;
  outline: none !important;
  cursor: pointer !important;
  
  &::-webkit-slider-thumb {
    appearance: none !important;
    width: 12px !important;
    height: 12px !important;
    background: #f4f1de !important;
    border-radius: 50% !important;
    cursor: pointer !important;
    border: 1px solid rgba(244, 241, 222, 0.3) !important;
  }
  
  &::-moz-range-thumb {
    width: 12px !important;
    height: 12px !important;
    background: #f4f1de !important;
    border-radius: 50% !important;
    cursor: pointer !important;
    border: 1px solid rgba(244, 241, 222, 0.3) !important;
  }
  
  &:hover::-webkit-slider-thumb {
    background: rgba(244, 241, 222, 0.9) !important;
  }
  
  &:hover::-moz-range-thumb {
    background: rgba(244, 241, 222, 0.9) !important;
  }
`;

const SpeedButton = styled(Button)`
  background: rgba(244, 241, 222, 0.1) !important;
  border: 1px solid rgba(244, 241, 222, 0.2) !important;
  color: #f4f1de !important;
  border-radius: 4px !important;
  padding: 2px 6px !important;
  font-size: 11px !important;
  min-width: 30px !important;
  height: 24px !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(244, 241, 222, 0.2) !important;
    border-color: rgba(244, 241, 222, 0.4) !important;
  }
  
  &.active {
    background: rgba(76, 175, 80, 0.3) !important;
    border-color: rgba(76, 175, 80, 0.5) !important;
  }
`;

const SpeedLabel = styled.span`
  font-size: 10px !important;
  color: rgba(244, 241, 222, 0.7) !important;
  margin-right: 3px !important;
`;

const VolumeLabel = styled.span`
  font-size: 10px !important;
  color: rgba(244, 241, 222, 0.7) !important;
  margin-right: 3px !important;
  min-width: 25px !important;
`;

const VolumeIcon = styled.div`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 16px !important;
  height: 16px !important;
  margin-left: 3px !important;
`;

const AudioStatusIndicator = styled.div`
  position: absolute !important;
  top: -2px !important;
  right: -2px !important;
  width: 8px !important;
  height: 8px !important;
  border-radius: 50% !important;
  background: ${props => {
    if (props.isPlaying) return '#4CAF50 !important';
    if (props.isPaused) return '#FF9800 !important';
    return 'transparent !important';
  }};
  border: 2px solid rgba(244, 241, 222, 0.8) !important;
  transition: all 0.3s ease !important;
`;

const AccordionHeaderContainer = styled.div`
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100% !important;
`;

const AccordionTitleContainer = styled.div`
  display: flex !important;
  align-items: center !important;
  flex: 1 !important;
`;

// Audio Control Component
const AudioControl = ({ text, sectionName, isInsideAccordion = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const isCurrentAudio = useCallback(() => {
    const status = audioManager.getPlayingStatus();
    return status.currentText === text;
  }, [text]);

  useEffect(() => {
    const removeListener = audioManager.addListener((status) => {
      if (status.currentText === text) {
        setIsPlaying(status.isPlaying);
        setIsPaused(status.isPaused);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
      }
    });

    return removeListener;
  }, [text]);

  const handleAudioToggle = useCallback(async (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    if (isPlaying) {
      if (isPaused) {
        audioManager.resume();
      } else {
        audioManager.pause();
      }
    } else {
      try {
        await audioManager.speak(text, {
          rate: 1.0,
          pitch: 1,
          volume: 0.8
        });
      } catch (error) {
        console.error('Erro ao reproduzir áudio:', error);
      }
    }
  }, [isPlaying, isPaused, text]);

  useEffect(() => {
    return () => {
      if (isCurrentAudio()) {
        audioManager.stop();
      }
    };
  }, [isCurrentAudio]);

  if (!audioManager.isSupported() || !text || text.trim() === '') {
    return null;
  }

  const getButtonClass = () => {
    if (isPlaying && !isPaused) return 'playing';
    if (isPaused) return 'paused';
    return '';
  };

  const getButtonTitle = () => {
    if (isPlaying) {
      return isPaused ? 'Retomar leitura do texto' : 'Pausar leitura do texto';
    }
    return 'Ouvir texto sendo lido';
  };

  const getButtonIcon = () => {
    if (!isPlaying) return <IoVolumeHigh size={16} />;
    if (isPaused) return <IoVolumeHigh size={16} />;
    return <IoPause size={16} />;
  };

  // Se estiver dentro do accordion, usa div ao invés de Button
  if (isInsideAccordion) {
    return (
      <div
        onClick={handleAudioToggle}
        title={getButtonTitle()}
        id={`instructions-audio-${sectionName}`}
        className={`audio-control-div ${getButtonClass()}`}
        style={{
          background: 'rgba(244, 241, 222, 0.1)',
          border: '1px solid rgba(244, 241, 222, 0.2)',
          color: '#f4f1de',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '10px',
          transition: 'all 0.3s ease',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          cursor: 'pointer'
        }}
      >
        {getButtonIcon()}
        <AudioStatusIndicator isPlaying={isPlaying} isPaused={isPaused} />
      </div>
    );
  }

  // Se não estiver dentro do accordion, usa Button normal
  return (
    <AudioButton
      onClick={handleAudioToggle}
      title={getButtonTitle()}
      id={`instructions-audio-${sectionName}`}
      className={getButtonClass()}
    >
      {getButtonIcon()}
      <AudioStatusIndicator isPlaying={isPlaying} isPaused={isPaused} />
    </AudioButton>
  );
};

const Instructions = ({ activityDescription, actionHelp }) => {
  const hasActivityDescription = activityDescription && activityDescription.trim() !== '';
  const hasActionHelp = actionHelp && actionHelp.trim() !== '';

  return (
    <InstructionsContainer id='modal-question-instructions'>
      <StyledAccordion id='modal-question-instructions-accordion'>
        {/* Seção de Atividade - só renderiza se houver descrição */}
        {hasActivityDescription && (
          <Accordion.Item id='modal-question-instructions-activity' eventKey='0'>
            <Accordion.Header id='modal-question-instructions-activity-header'>
              <AccordionHeaderContainer>
                <AccordionTitleContainer>
                  <CustomAccordionTitle id='modal-question-instructions-activity-title'>
                    Entenda a atividade.
                  </CustomAccordionTitle>
                </AccordionTitleContainer>
                <AudioControl 
                  text={activityDescription} 
                  sectionName="activity" 
                  isInsideAccordion={true}
                />
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body id='modal-question-instructions-activity-body'>
              <Element id='modal-question-instructions-activity-content'>
                {activityDescription}
              </Element>
            </Accordion.Body>
          </Accordion.Item>
        )}
        
        {/* Seção de Ação - só renderiza se houver ajuda */}
        {hasActionHelp && (
          <Accordion.Item id='modal-question-instructions-action' eventKey='1'>
            <Accordion.Header id='modal-question-instructions-action-header'>
              <AccordionHeaderContainer>
                <AccordionTitleContainer>
                  <CustomAccordionTitle id='modal-question-instructions-action-title'>
                    O que e como fazer?
                  </CustomAccordionTitle>
                </AccordionTitleContainer>
                <AudioControl 
                  text={actionHelp} 
                  sectionName="action" 
                  isInsideAccordion={true}
                />
              </AccordionHeaderContainer>
            </Accordion.Header>
            <Accordion.Body id='modal-question-instructions-action-body'>
              <Element id='modal-question-instructions-action-content'>
                {actionHelp}
              </Element>
            </Accordion.Body>
          </Accordion.Item>
        )}
      </StyledAccordion>
    </InstructionsContainer>
  );
};

export default Instructions;

